/**
 * File validation utilities for FileDropzone components
 */

/**
 * Validates if a file matches the accepted file types
 * @param {File} file - Browser File object to validate
 * @param {string} accept - Accepted file types (comma-separated MIME types and extensions)
 * @param {boolean} enableLogging - Whether to log validation details
 * @returns {boolean} - True if file is accepted, false otherwise
 */
export const validateFileType = (file, accept, enableLogging = false) => {
	if (!accept) {
		if (enableLogging) {
			console.log('FileValidation: No accept parameter provided, allowing all files');
		}
		return true;
	}
	
	const acceptedTypes = accept.split(',').map(type => type.trim());
	const fileType = file.type;
	const fileName = file.name.toLowerCase();
	
	if (enableLogging) {
		console.log('FileValidation Details:', {
			fileName: file.name,
			fileType: fileType,
			fileSize: file.size,
			acceptedTypes: acceptedTypes
		});
	}
	
	const isValid = acceptedTypes.some(acceptType => {
		let matches = false;
		let validationType = '';
		
		// Extension-based validation (e.g., ".pdf", ".jpg")
		if (acceptType.startsWith('.')) {
			matches = fileName.endsWith(acceptType.toLowerCase());
			validationType = 'extension';
		}
		// Wildcard MIME type validation (e.g., "image/*", "video/*")
		else if (acceptType.includes('*')) {
			const baseType = acceptType.split('/')[0];
			matches = fileType.startsWith(baseType + '/');
			validationType = 'wildcard';
		}
		// Exact MIME type validation (e.g., "application/pdf")
		else {
			matches = fileType === acceptType;
			validationType = 'exact';
		}
		
		if (enableLogging && matches) {
			console.log(`FileValidation: Match found via ${validationType} - ${acceptType}`);
		}
		
		return matches;
	});
	
	if (enableLogging) {
		console.log(`FileValidation Result: ${isValid ? 'ACCEPTED' : 'REJECTED'} - ${file.name}`);
	}
	
	return isValid;
};

/**
 * Validates multiple files against accepted file types
 * @param {FileList|Array} files - Files to validate
 * @param {string} accept - Accepted file types (comma-separated MIME types and extensions)
 * @param {boolean} enableLogging - Whether to log validation details
 * @returns {boolean} - True if all files are accepted, false otherwise
 */
export const validateMultipleFileTypes = (files, accept, enableLogging = false) => {
	if (!accept) {
		if (enableLogging) {
			console.log('FileValidation: No accept parameter provided, allowing all files');
		}
		return true;
	}
	
	if (enableLogging) {
		console.log(`FileValidation: Validating ${files.length} files against accept: "${accept}"`);
	}
	
	const results = Array.from(files).map((file, index) => {
		const isValid = validateFileType(file, accept, enableLogging);
		if (enableLogging) {
			console.log(`FileValidation: File ${index + 1}/${files.length} - ${file.name}: ${isValid ? 'VALID' : 'INVALID'}`);
		}
		return isValid;
	});
	
	const allValid = results.every(result => result);
	
	if (enableLogging) {
		console.log(`FileValidation: Overall result for ${files.length} files: ${allValid ? 'ALL VALID' : 'SOME INVALID'}`);
	}
	
	return allValid;
};

/**
 * Full file validation including type, size, duplicates, and count limits
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @param {string} options.accept - Accepted file types
 * @param {Array} options.allFiles - All existing files for duplicate checking
 * @param {number} options.maxFiles - Maximum number of files allowed
 * @param {number} options.maxFileSize - Maximum file size in MB (0 = unlimited)
 * @param {string} options.fileHash - Hash of the current file for duplicate checking
 * @param {boolean} options.enableLogging - Whether to log validation details
 * @returns {Object} - Validation result with isValid and error message
 */
export const validateFile = (file, options = {}) => {
	const { accept, allFiles = [], maxFiles, maxFileSize = 0, fileHash, enableLogging = false } = options;
	
	if (enableLogging) {
		console.log('FileValidation: Full validation started for:', {
			fileName: file.name,
			fileSize: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
			maxFiles: maxFiles,
			maxFileSize: maxFileSize ? `${maxFileSize} MB` : 'unlimited',
			currentFileCount: allFiles.length,
			hasFileHash: !!fileHash
		});
	}
	
	// File size check (convert MB to bytes for comparison)
	if (maxFileSize > 0) {
		const maxSizeBytes = maxFileSize * 1024 * 1024;
		if (file.size > maxSizeBytes) {
			if (enableLogging) {
				console.log(`FileValidation: REJECTED - File size exceeds limit (${(file.size / (1024 * 1024)).toFixed(2)} MB / ${maxFileSize} MB)`);
			}
			return {
				isValid: false,
				error: `Datei zu groß (max. ${maxFileSize} MB)`
			};
		}
	}
	
	// Max files check
	if (maxFiles && allFiles.length > maxFiles) {
		if (enableLogging) {
			console.log(`FileValidation: REJECTED - Max files exceeded (${allFiles.length}/${maxFiles})`);
		}
		return {
			isValid: false,
			error: `Maximal ${maxFiles} Dateien erlaubt`
		};
	}
	
	// File type check
	if (!validateFileType(file, accept, enableLogging)) {
		if (enableLogging) {
			console.log('FileValidation: REJECTED - File type not allowed');
		}
		return {
			isValid: false,
			error: 'Dateityp nicht erlaubt'
		};
	}
	
	// Duplicate check (if fileHash is provided)
	if (fileHash && allFiles.some(f => f.fileHash === fileHash)) {
		if (enableLogging) {
			console.log('FileValidation: REJECTED - Duplicate file detected');
		}
		return {
			isValid: false,
			error: 'Datei bereits vorhanden'
		};
	}
	
	if (enableLogging) {
		console.log('FileValidation: ACCEPTED - All checks passed');
	}
	
	return {
		isValid: true,
		error: null
	};
};
